/**
 *  Translation service: https://www.locize.app/
 *  Locize documentation: https://docs.locize.com/integration/instrumenting-your-code
 *  Credentials: https://teampass.eggcooperation.com
 *  */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import Backend from 'i18next-locize-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpBackend from 'i18next-http-backend'
import DayJS from './helpers/DayJS'
import es from 'dayjs/locale/es'
import en from 'dayjs/locale/en'

const isProduction = process.env.REACT_APP_ENV === 'production'

// Add supported languages constant
const SUPPORTED_LANGUAGES = ['en', 'es', 'pt', 'gl']
const DEFAULT_LANGUAGE = 'es'

const locizeOptions = {
    projectId: `${process.env.REACT_APP_LOCIZE_PROJECT_ID}`,
    apiKey: `${process.env.REACT_APP_LOCIZE_API_KEY}`,
    referenceLng: process.env.REACT_APP_LOCIZE_REFLNG ?? DEFAULT_LANGUAGE,
    version: process.env.REACT_APP_LOCIZE_VERSION ?? 'latest',
}

i18n.use(initReactI18next)
    .use(isProduction ? Backend : (HttpBackend as any))
    .use(LanguageDetector)
    .init(
        {
            debug: false,
            ns: [],
            load: 'languageOnly',
            fallbackLng: DEFAULT_LANGUAGE,
            supportedLngs: SUPPORTED_LANGUAGES,
            backend: isProduction
                ? locizeOptions
                : {
                      loadPath: '/locales/{{lng}}/{{ns}}.json',
                  },
            interpolation: { escapeValue: false },
            react: {
                useSuspense: true,
            },
            cache: ['localStorage'],
            detection: {
                order: [
                    'querystring',
                    'cookie',
                    'localStorage',
                    'sessionStorage',
                    'navigator',
                    'htmlTag',
                    'path',
                    'subdomain',
                ],
                caches: ['localStorage', 'cookie'],
                cookieOptions: { path: '/', sameSite: 'strict' },
            },
        },
        err => {
            if (err) return console.log('something went wrong loading', err)
        }
    )

i18n.on('missingKey', (languages, namespace, key, res) => !isProduction && console.error({ languages, namespace, key, res }))
i18n.on('languageChanged', lng => {
    !isProduction && console.info(`Language changed to ${lng}`)
    const userLanguage = lng.split('-').at(0)
    userLanguage === 'es' ? DayJS.locale(es) : DayJS.locale(en)
})

// Export a function to get languages from Locize (only for production)
export const getLocizeLanguages = async (): Promise<AvailableLanguages | null> => {
    try {
        let backend = i18n.services.backendConnector.backend;
        if (!isProduction) {
            backend = new Backend(locizeOptions);
        }
        const languages = await backend.getLanguages();
        return languages;
    } catch (error) {
        console.error('Error fetching languages from Locize:', error);
        return null;
    }
};

export default i18n

export const plural = {
    count: 10,
}

export type AvailableLanguages = Record<
    string,
    {
        name: string
        nativeName: string
        isReferenceLanguage: boolean
        translated: {
            latest: number
        }
    }
>
